import { Layer } from 'shared/map-exports';
import { MapLayerProps, MapClickGetPois } from 'features/mapping/mapping.types';
import { NgfsDetectionPoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { addVisible } from 'shared/addVisible';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { usePoisState } from 'state/usePoisState';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { getWatchdutyTileUrl } from '../layers.utils';
import {
  SOURCE_LAYER,
  SOURCE_ID,
  LINE_LAYER_ID,
  FILL_LAYER_ID,
  SELECTED_FILL_LAYER_ID,
  SELECTED_STROKE_LAYER_ID,
  REFRESH_INTERVAL_SECONDS,
} from './NgfsDetectionsLayer.constants';
import {
  getInteractiveFillStyle,
  getLineStyle,
} from './NgfsDetectionsLayer.utils';

type NgfsDetectionsLayerProps = MapLayerProps & {
  interactive?: boolean;
};

type NgfsDetectionFeatureProperties = {
  frp: number; // Fire Radiative Power in megawatts
  type_description: string;
  acquisition_epoch: number; // Epoch seconds
};

export const ngfsDetectionClickHandler: MapClickGetPois<
  NgfsDetectionFeatureProperties
> = (geoJsonFeatures) =>
  geoJsonFeatures.map((feature) => {
    const metadata: NgfsDetectionPoi = {
      frp: feature.properties.frp,
      typeDescription: feature.properties.type_description,
      acquisitionEpoch: feature.properties.acquisition_epoch,
    };
    return {
      type: 'ngfsDetection',
      id: feature.id,
      metadata,
    };
  });

export const NgfsDetectionsLayer = (
  props: NgfsDetectionsLayerProps,
): JSX.Element => {
  const { visible, interactive = true } = props;
  const { selectedPoi } = usePoisState();

  const selectedId =
    selectedPoi?.type === 'ngfsDetection' ? (selectedPoi.id ?? 0) : 0;

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: { 'source-layer': SOURCE_LAYER },
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: { 'source-layer': SOURCE_LAYER },
  });

  return (
    <VisibilityToggledSource
      id={SOURCE_ID}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      visible={visible}
    >
      {/* Transparent interactive fill  layer */}
      <Layer
        id={FILL_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        metadata={{ interactive }}
        {...addVisible(getInteractiveFillStyle(), visible)}
      />

      <Layer
        id={LINE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getLineStyle(), visible)}
      />

      <Layer
        id={SELECTED_FILL_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(selectedFillStyle, visible)}
      />
      <Layer
        id={SELECTED_STROKE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(selectedStrokeStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};
